import React, { useState, useEffect } from 'react';
import { Box, Modal, TextField, Typography, IconButton, MenuItem, Select, FormControl, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CommonModalTitle from '../../../form-components/CommonModalTitle';
import CommonModalButton from '../../../form-components/CommonModalButton';
import {
  ROOM_TYPE,
  ROOM_TYPE_OPTIONS,
  DEFAULT_FLAG_FIELD,
  DEFAULT_TARGETS_BY_ROOM_TYPE,
  DEFAULT_TARGET_LABELS,
} from '../../../constants/roomTypes';

// 스케줄 등록 모달과 같은 프레임(다크 헤더 + 라운드 카드 + 하단 바)을 쓴다.
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '440px',
  maxWidth: 'calc(100vw - 32px)',
  backgroundColor: '#fff',
  borderRadius: '10px',
  overflow: 'hidden',
  outline: 'none',
});

const ModalHeader = styled(Box)({
  backgroundColor: '#111',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '17px 20px',
});

const ModalCloseIcon = styled(CloseIcon)({
  color: '#fff',
  cursor: 'pointer',
  fontSize: '22px',
});

const ContentArea = styled(Box)({
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const FieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const FieldLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
});

const HintText = styled(Typography)({
  fontSize: '12px',
  color: '#8c8c8c',
  lineHeight: 1.5,
});

// 입력 계열은 라운드 8 + 포커스 시 액센트 컬러로 통일한다.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#e5e5e5' },
    '&:hover fieldset': { borderColor: '#c9c9c9' },
    '&.Mui-focused fieldset': { borderColor: '#4AB3BC', borderWidth: '1px' },
  },
};

const StepperButton = styled(IconButton)({
  width: '38px',
  height: '38px',
  borderRadius: '8px',
  border: '1px solid #e5e5e5',
  color: '#262626',
  '&:hover': { backgroundColor: '#f5f5f5' },
  '&.Mui-disabled': { borderColor: '#f0f0f0', color: '#d9d9d9' },
});

const LimitToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  padding: '6px 8px 6px 12px',
});

const FooterBar = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '15px 20px',
  borderTop: '1px solid #f0f0f0',
});

const accentSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
};

/**
 * 강의실 추가·수정 공용 모달.
 * 값만 모아 onSave 로 넘기고, 저장 방식(생성/수정·영향 확인)은 부모가 정한다.
 */
function ClassroomEdit({
  open,
  onClose,
  onSave,
  isLoading,
  mode = 'create',
  initialValues = null,
}) {
  const { t } = useTranslation();
  const [roomName, setRoomName] = useState('');
  const [roomType, setRoomType] = useState(ROOM_TYPE.ALL);
  // 제한 여부는 토글로 따로 받는다 — 숫자 칸에 '제한없음'을 섞으면 무엇을 입력해야 하는지 헷갈린다.
  const [unlimited, setUnlimited] = useState(true);
  const [maxSession, setMaxSession] = useState(1);
  // 레슨구분별 기본 강의실 지정 — 고를 수 있는 강의실이 여럿일 때 우선 선택된다.
  const [defaultTargets, setDefaultTargets] = useState([]);

  useEffect(() => {
    if (!open) return;
    setRoomName(initialValues?.roomName ?? '');
    setRoomType(initialValues?.roomType || ROOM_TYPE.ALL);
    setUnlimited(initialValues?.maxSession == null);
    setMaxSession(initialValues?.maxSession ?? 1);
    setDefaultTargets(
      Object.keys(DEFAULT_TARGET_LABELS).filter(
        target => initialValues?.[DEFAULT_FLAG_FIELD[target]] === 'Y',
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 강의실 유형을 바꾸면 그 유형이 못 받는 레슨구분의 기본 지정은 떨어져 나간다.
  const availableTargets = DEFAULT_TARGETS_BY_ROOM_TYPE[roomType] || [];
  const effectiveTargets = defaultTargets.filter(target => availableTargets.includes(target));

  const handleSave = () => {
    if (!roomName.trim()) return;
    onSave({
      roomName: roomName.trim(),
      roomType,
      maxSession: unlimited ? null : maxSession,
      defaultForPrivateYn: effectiveTargets.includes('PRIVATE_LESSON') ? 'Y' : 'N',
      defaultForGroupYn: effectiveTargets.includes('GROUP_LESSON') ? 'Y' : 'N',
      defaultForConsultationYn: effectiveTargets.includes('CONSULTATION') ? 'Y' : 'N',
    });
  };

  const selectSx = {
    borderRadius: '8px',
    fontSize: '14px',
    '& fieldset': { borderColor: '#e5e5e5' },
    '&:hover fieldset': { borderColor: '#c9c9c9' },
    '&.Mui-focused fieldset': { borderColor: '#4AB3BC', borderWidth: '1px' },
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <ModalContainer>
        <ModalHeader>
          <CommonModalTitle>{mode === 'edit' ? t('강의실 수정') : t('강의실 추가')}</CommonModalTitle>
          <ModalCloseIcon onClick={onClose} />
        </ModalHeader>

        <ContentArea>
          <FieldGroup>
            <FieldLabel>{t('강의실 명')}</FieldLabel>
            <TextField
              size="small"
              fullWidth
              value={roomName}
              onChange={e => setRoomName(e.target.value)}
              placeholder={t('그룹레슨 A반')}
              autoFocus
              sx={inputSx}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t('강의실 유형')}</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select value={roomType} onChange={e => setRoomType(e.target.value)} sx={selectSx}>
                {ROOM_TYPE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t('기본 강의실')}</FieldLabel>
            {availableTargets.map(target => (
              <LimitToggleRow key={target}>
                <Typography sx={{ fontSize: '14px', color: '#262626' }}>
                  {t(DEFAULT_TARGET_LABELS[target])}
                </Typography>
                <Switch
                  checked={effectiveTargets.includes(target)}
                  onChange={e =>
                    setDefaultTargets(prev =>
                      e.target.checked ? [...prev, target] : prev.filter(item => item !== target),
                    )
                  }
                  sx={accentSwitchSx}
                />
              </LimitToggleRow>
            ))}
            <HintText>
              {t('스케줄을 등록할 때 이 강의실이 먼저 선택됩니다. 레슨구분마다 한 곳만 지정할 수 있습니다')}
            </HintText>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t('동시간 최대 수업')}</FieldLabel>
            <LimitToggleRow>
              <Typography sx={{ fontSize: '14px', color: '#262626' }}>{t('제한없음')}</Typography>
              <Switch checked={unlimited} onChange={e => setUnlimited(e.target.checked)} sx={accentSwitchSx} />
            </LimitToggleRow>
            {!unlimited && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <StepperButton onClick={() => setMaxSession(prev => Math.max(1, prev - 1))} disabled={maxSession <= 1}>
                  <RemoveIcon fontSize="small" />
                </StepperButton>
                <TextField
                  size="small"
                  value={maxSession}
                  onChange={e => {
                    const value = e.target.value;
                    if (/^\d+$/.test(value) && Number(value) >= 1) setMaxSession(Number(value));
                  }}
                  sx={{ ...inputSx, width: '88px', '& input': { textAlign: 'center', fontWeight: 600 } }}
                />
                <StepperButton onClick={() => setMaxSession(prev => prev + 1)}>
                  <AddIcon fontSize="small" />
                </StepperButton>
                <Typography sx={{ fontSize: '14px', color: '#595959' }}>{t('개')}</Typography>
              </Box>
            )}
            <HintText>
              {unlimited
                ? t('동시간에 이 강의실을 쓰는 수업 수를 제한하지 않습니다')
                : t('동시간에 이 강의실을 쓰는 수업이 설정한 수를 넘으면 등록이 막힙니다')}
            </HintText>
          </FieldGroup>
        </ContentArea>

        <FooterBar>
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave} disabled={!roomName.trim() || isLoading}>
            {t('저장')}
          </CommonModalButton>
        </FooterBar>
      </ModalContainer>
    </Modal>
  );
}

export default ClassroomEdit;
